import {
  EVENT_APP_FATAL_ERROR,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_APP_SET_LOADING_PROGRESS,
  EVENT_APP_TERMINATING,
  type OnAppTerminatingData,
} from "@classdojo/monsterverse-utils";
import { translate, type TranslationKey } from "@monsterverse/i18n";

import type { PCAppEventHandler } from "./PCAppEventHandler";

type LoadingStage = { str: TranslationKey; start: number };

export class LoadingProgress {
  private _progressStages: LoadingStage[] = [
    {
      str: "common.loading.loading_island",
      start: 0.0,
    },
    {
      str: "common.loading.loading_monsters",
      start: 0.55,
    },
    {
      str: "common.loading.loading_finished",
      start: 1.0,
    },
  ];

  private _displayedProgress = 0.0;
  private _actualProgress = 0.0;
  private _previousTime = 0.0;
  private _loadingEl: HTMLElement;
  private _progressBarTextEl: HTMLElement;
  private _progressBarLeftEl: HTMLElement;
  private _progressBarMiddleEl: HTMLElement;
  private _progressBarRightEl: HTMLElement;
  private _nextAnimationFrame = 0; // If set at runtime, this should be a non-zero value.

  constructor(loadingEl: HTMLElement, pcAppEventHandler: PCAppEventHandler) {
    this._loadingEl = loadingEl;
    this._progressBarTextEl = loadingEl.querySelector(".progress-bar")?.firstElementChild as HTMLElement;
    this._progressBarLeftEl = loadingEl.querySelector(".progressLeftMove") as HTMLElement;
    this._progressBarMiddleEl = loadingEl.querySelector(".progressMiddleMove") as HTMLElement;
    this._progressBarRightEl = loadingEl.querySelector(".progressRightMove") as HTMLElement;
    this.setLoadingProgress(0);
    this._resetRendering();

    if (window.NO_LOADING_SCREEN) {
      this.setLoadingProgress(2);
      this._hide();
    }

    pcAppEventHandler.on(EVENT_APP_LOADING_COMPLETE, () => {
      this.setLoadingProgress(2);
    });

    pcAppEventHandler.on(EVENT_APP_FATAL_ERROR, () => {
      this._cancelDraw();
      this._hide();
    });

    pcAppEventHandler.on(EVENT_APP_SET_LOADING_PROGRESS, (stage: unknown) => {
      this.setLoadingProgress(stage as number);
    });

    pcAppEventHandler.on(EVENT_APP_TERMINATING, (data: unknown) => {
      const handler = data as OnAppTerminatingData;
      if (handler) {
        const { exitingMonsterverse, customizer } = handler;
        if (!exitingMonsterverse && !customizer) {
          this._show();
          this.setLoadingProgress(0);
        }
      }
    });
  }

  setLoadingProgress(stage: number) {
    if (!this._progressBarTextEl) return;
    const { str, start } = this._progressStages[stage];
    this._progressBarTextEl.textContent = translate(str) || "Loading island…";
    this._actualProgress = start;
  }

  private _requestDraw() {
    this._nextAnimationFrame = requestAnimationFrame((t) => this._draw(t));
  }

  private _cancelDraw() {
    if (this._nextAnimationFrame) cancelAnimationFrame(this._nextAnimationFrame);
  }

  private _resetRendering() {
    this._actualProgress = 0.0;
    this._displayedProgress = 0.0;
    this._previousTime = 0.0;

    this._cancelDraw();
    this._requestDraw();
  }

  private _show() {
    this._loadingEl.classList.remove("display-none");
    this._loadingEl.classList.remove("opacity0");

    this._resetRendering();
  }

  private _hide() {
    const setDisplayNone = () => this._loadingEl.classList.add("display-none");
    const onHide = () => {
      clearTimeout(backupElementRemoval);
      setDisplayNone();
      this._loadingEl.removeEventListener("transitionend", onHide);
    };

    const backupElementRemoval = setTimeout(() => setDisplayNone(), 250);
    this._loadingEl.addEventListener("transitionend", onHide);
    this._loadingEl.classList.add("opacity0");
  }

  private _draw(t: number) {
    if (!this._previousTime) {
      this._previousTime = t;
      this._requestDraw();
      return;
    }

    const delta = t - this._previousTime;
    this._previousTime = t;

    if (this._displayedProgress < this._actualProgress) {
      // Quickly advance progress bar as progress has advanced past current point
      this._displayedProgress += delta / 2000;

      // Eventually we want displayedProgress to catch up to actualProgress.
      if (this._actualProgress - this._displayedProgress < 0.001) {
        this._displayedProgress = this._actualProgress;
      }
    } else {
      // Creep progress bar slowly until progress advances to the next stage
      const nextStageStart =
        this._progressStages.find((s) => {
          return s.start > this._actualProgress;
        })?.start ?? 1;

      this._displayedProgress += this._ease(nextStageStart - this._displayedProgress) * (delta / 16000);
    }

    //Calculate the percentages that display on the left, center, and right sections
    //The left and right are a constant 180px width and the center fills in the difference
    //This calculation keeps the bar moving smoothly as if it was all a single image
    const sideWidth = 180;
    const leftSidePercent = sideWidth / window.innerWidth;
    const rightSidePercent = 1 - leftSidePercent;
    const sideScale = 1.0 / leftSidePercent;
    const middleScale = 1.0 / (1 - leftSidePercent * 2);

    const leftProgress = this._displayedProgress < leftSidePercent ? this._displayedProgress * sideScale : 1;
    const middleProgress =
      this._displayedProgress < leftSidePercent
        ? 0
        : this._displayedProgress > rightSidePercent
          ? 1
          : (this._displayedProgress - leftSidePercent) * middleScale;
    const rightProgress =
      this._displayedProgress < rightSidePercent ? 0 : (this._displayedProgress - rightSidePercent) * sideScale;
    this._progressBarLeftEl.style.transform = `translateX(${(-1 + leftProgress) * 100}%)`;
    this._progressBarMiddleEl.style.transform = `translateX(${(-1 + middleProgress) * 100}%)`;
    this._progressBarRightEl.style.transform = `translateX(${(-1 + rightProgress) * 100}%)`;

    if (this._displayedProgress < 1) {
      this._requestDraw();
    } else {
      this._hide();
    }
  }

  private _ease(x: number) {
    return 1 - (1 - x) * (1 - x); // Quadratic Ease-out
  }
}
